import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
  Tabs,
  Tab,
  Divider,
  InputAdornment,
  IconButton
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { authService } from "../api/services";
import { useGoogleLogin } from "@react-oauth/google";
import GoogleIcon from "@mui/icons-material/Google";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const TabPanel = ({ children, value, index, ...other }) => (
  <div hidden={value !== index} {...other}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const TraditionalLogin = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Estados para login
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  // Estados para registro
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    last_name: "",
  });

  // Google Login Handler
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      setLoading(true);
      setError("");
      try {
        const { data } = await authService.googleAuth(response.access_token);
        login(data);
        navigate("/");
      } catch (err) {
        setError(err.response?.data?.error || "Error al iniciar sesión con Google");
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setError("Error al iniciar sesión con Google");
      setLoading(false);
    },
  });

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!loginData.email || !loginData.password) {
      setError("Por favor completa todos los campos");
      return;
    }

    if (!validateEmail(loginData.email)) {
      setError("Por favor ingresa un email válido");
      return;
    }

    setLoading(true);

    try {
      const { data } = await authService.traditionalLogin(loginData);
      login(data);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.error || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!registerData.email || !registerData.password || !registerData.confirmPassword) {
      setError("Por favor completa todos los campos obligatorios");
      return;
    }

    if (!validateEmail(registerData.email)) {
      setError("Por favor ingresa un email válido");
      return;
    }

    if (registerData.password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    setLoading(true);

    try {
      const { data } = await authService.register({
        email: registerData.email,
        password: registerData.password,
        first_name: registerData.first_name,
        last_name: registerData.last_name,
      });

      setSuccess(data.message || "Usuario registrado exitosamente");
      
      // Auto-login después del registro
      setTimeout(() => {
        login(data);
        navigate("/");
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || "Error al registrar usuario");
    } finally {
      setLoading(false);
    }
  };

  const GoogleButton = () => (
    <Button
      variant="outlined"
      fullWidth
      startIcon={<GoogleIcon />}
      onClick={() => handleGoogleLogin()}
      disabled={loading}
      sx={{ 
        mb: 3, 
        py: 1.5,
        textTransform: 'none',
        fontSize: '1rem',
        borderColor: '#dadce0',
        color: '#3c4043',
        '&:hover': {
          borderColor: '#d2e3fc',
          backgroundColor: 'rgba(66, 133, 244, 0.04)',
        }
      }}
    >
      Continuar con Google
    </Button>
  );

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ overflow: "hidden", borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => {
              setTabValue(newValue);
              setError("");
              setSuccess("");
            }}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Iniciar Sesión" />
            <Tab label="Registrarse" />
          </Tabs>
        </Box>

        {/* Pestaña de Login */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h5" gutterBottom align="center" fontWeight="bold">
            Bienvenido de nuevo
          </Typography>
          
          <Box sx={{ mt: 3 }}>
            <GoogleButton />
            
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                O con tu email
              </Typography>
            </Divider>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box component="form" onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={loginData.email}
              onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Contraseña"
              type={showPassword ? "text" : "password"}
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2" underline="hover">
                ¿Olvidaste tu contraseña?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading}
              size="large"
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Iniciar Sesión"}
            </Button>
          </Box>
        </TabPanel>

        {/* Pestaña de Registro */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h5" gutterBottom align="center" fontWeight="bold">
            Crear una cuenta
          </Typography>

          <Box sx={{ mt: 3 }}>
            <GoogleButton />
            
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                O regístrate con tu email
              </Typography>
            </Divider>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box component="form" onSubmit={handleRegister}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Nombre"
                value={registerData.first_name}
                onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Apellido"
                value={registerData.last_name}
                onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                margin="normal"
              />
            </Box>
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={registerData.email}
              onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Contraseña"
              type={showPassword ? "text" : "password"}
              value={registerData.password}
              onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
              margin="normal"
              required
              helperText="Mínimo 6 caracteres"
            />
            <TextField
              fullWidth
              label="Confirmar Contraseña"
              type={showPassword ? "text" : "password"}
              value={registerData.confirmPassword}
              onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
              margin="normal"
              required
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading}
              size="large"
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Registrarse"}
            </Button>
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default TraditionalLogin;
